import { Card, CardContent } from '@design-system';
import { carePhaseDefs } from '@shared';
import type { WalkthroughMonthStatus } from '@shared/consumerPlan';
import { DetailList, PaymentSplitBar } from '../components/figures';
import { StepFrame } from '../components/step-frame';
import { useCareWalkthrough } from '../hooks/use-care-walkthrough';
import { useConsumerFlow } from '../hooks/use-consumer-flow';
import { CARE_ENVIRONMENT_LABELS } from '../lib/copy';
import { money } from '../lib/format';
import type { ConsumerStepProps } from './step-props';

const STATUS_COPY: Record<WalkthroughMonthStatus, string> = {
  beforeTrigger: 'Care hasn’t reached the policy’s benefit trigger yet.',
  waitingPeriod: 'The policy’s waiting period is in progress.',
  paying: 'The policy is paying benefits.',
  notPaying: 'No benefit payment is available this month.',
};

export function WalkthroughStep({ clientId }: ConsumerStepProps) {
  const { goTo, back } = useConsumerFlow(clientId);
  const { moment, position, momentCount, isLastMoment, showNextMoment, showPreviousMoment, scenario } =
    useCareWalkthrough(clientId);
  if (!moment || !scenario) return null;

  const phaseLabel = carePhaseDefs[moment.carePhase].label;

  return (
    <StepFrame
      step="walkthrough"
      title="Who pays at this point in care?"
      subtitle={`Moment ${position + 1} of ${momentCount} · ${phaseLabel}`}
      onBack={() => (position > 0 ? showPreviousMoment() : back('walkthrough-summary'))}
      help={
        <>
          <p>In a real claim, you contact the insurer, document qualifying care, and meet the policy’s waiting period.</p>
          <p>We assume care qualifies once you need help with two or more daily activities. Actual eligibility depends on policy terms.</p>
        </>
      }
      primary={{
        label: isLastMoment ? 'See the summary' : 'Next moment',
        onClick: isLastMoment ? () => goTo('walkthrough-summary') : showNextMoment,
      }}
      next={isLastMoment ? 'review the whole event.' : 'see what changes as care progresses.'}
    >
      <Card data-testid="consumer-walkthrough-moment">
        <CardContent className="flex flex-col gap-4">
          <span className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
            Month {moment.monthIndex + 1} · {CARE_ENVIRONMENT_LABELS[moment.careEnvironment]}
          </span>
          <h2 className="text-lg font-semibold text-gray-950">{STATUS_COPY[moment.status]}</h2>
          <PaymentSplitBar
            total={moment.careCost}
            insurance={moment.paidByInsurance}
            label={`${money(moment.paidByInsurance)} from insurance, ${money(moment.stillNeeded)} still needed`}
          />
          <DetailList
            rows={[
              { label: 'Care bill this month', value: money(moment.careCost) },
              { label: 'Paid with insurance money', value: money(moment.paidByInsurance) },
              ...(moment.stillNeeded > 0.5 ? [{ label: 'Still needed for this month', value: money(moment.stillNeeded) }] : []),
            ]}
          />
        </CardContent>
      </Card>
      <p className="text-muted-foreground text-sm">Family help is already reflected in the bill.</p>
    </StepFrame>
  );
}
